import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/date';

const statusBadge = (status: string, t: (k: string, d: string) => string) => {
  if (status === 'ACCEPTED') return <Badge className="bg-green-100 text-green-700 border-green-200">{t('helperIntents.accepted', '已接受')}</Badge>;
  if (status === 'REJECTED') return <Badge className="bg-red-100 text-red-700 border-red-200">{t('helperIntents.rejected', '已婉拒')}</Badge>;
  if (status === 'EXPIRED')  return <Badge variant="secondary">{t('helperIntents.expired', '已過期')}</Badge>;
  return <Badge className="bg-amber-100 text-amber-700 border-amber-200">{t('helperIntents.pending', '待回覆')}</Badge>;
};

const HelperIntentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [intents, setIntents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<number | null>(null);

  useEffect(() => {
    if (authLoading || !user?.id || user.role !== 'HELPER') return;
    // helperProfileId = user.id (HelperProfile uses @MapsId)
    apiService.interviewIntents.getByHelper(user.id)
      .then((res: any) => { if (res.success && res.data) setIntents(res.data); })
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (!authLoading && (!user || user.role !== 'HELPER')) return <Navigate to="/home" replace />;
  if (authLoading || loading) return (
    <div className="flex min-h-[calc(100vh-64px)] items-center justify-center">
      <Loading message={t('common.loading', '載入中...')} />
    </div>
  );

  const handleAccept = async (intent: any) => {
    if (!intent.acceptToken) return;
    setActing(intent.id);
    try {
      await apiService.interviewIntents.accept(intent.acceptToken);
      setIntents((prev) => prev.map((i) => i.id === intent.id ? { ...i, status: 'ACCEPTED' } : i));
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (intent: any) => {
    if (!intent.acceptToken) return;
    setActing(intent.id);
    try {
      await apiService.interviewIntents.reject(intent.acceptToken);
      setIntents((prev) => prev.map((i) => i.id === intent.id ? { ...i, status: 'REJECTED' } : i));
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('helperIntents.title', '收到的面試邀請')}（{intents.length}）</CardTitle>
          <p className="text-sm text-muted-foreground">{t('helperIntents.subtitle', '僱主發出的面試意向，請在 7 天內回覆')}</p>
        </CardHeader>
        <CardContent>
          {intents.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
              {t('helperIntents.noIntents', '尚未收到任何面試邀請。')}
            </div>
          ) : (
            <div className="space-y-3">
              {intents.map((intent: any) => (
                <div key={intent.id} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <button
                      type="button"
                      className="font-medium text-left hover:underline"
                      onClick={() => navigate(`/job-postings?keyword=${encodeURIComponent(intent.employerName || '')}`)}
                    >
                      {intent.employerName || t('helperIntents.unknownEmployer', '未知僱主')}
                    </button>
                    <p className="text-xs text-muted-foreground">{t('helperIntents.sentAt', '邀請日期')}：{formatDateTime(intent.createdAt)}</p>
                    {intent.respondedAt && (
                      <p className="text-xs text-muted-foreground">{t('helperIntents.respondedAt', '回覆日期')}：{formatDateTime(intent.respondedAt)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(intent.status, t)}
                    {intent.status === 'PENDING' && (
                      <>
                        <Button
                          size="sm"
                          className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                          disabled={acting === intent.id}
                          onClick={() => handleAccept(intent)}
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          {t('helperIntents.accept', '接受')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 border-red-300 text-red-600 hover:bg-red-50"
                          disabled={acting === intent.id}
                          onClick={() => handleReject(intent)}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          {t('helperIntents.reject', '婉拒')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelperIntentsPage;
